import { Link } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // All work-related actions redirect to work URL
  const handleWorkAction = () => {
    window.location.href = process.env.REACT_APP_WORK_URL;
  };

  const handleOpportunitiesClick = () => {
    window.location.href = `${process.env.REACT_APP_WORK_URL}/opportunities`;
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#f9fafc",
          color: "#0f172a",
          borderBottom: "1px solid #e2e8f0",
          px: 2,
        }}
      >
        <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
          {/* Left side - Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ textDecoration: "none" }}>
              <Box
                component="img"
                src="/TalentMatch-logo-transparent.png"
                alt="Logo"
                sx={{ 
                  height: { xs: 24, md: 36 }, 
                  width: "auto", 
                  mr: 1 
                }}
                style={{ marginTop: 10 }}
              />
            </Link>

            {/* Desktop Navigation - Hidden on mobile */}
            {!isMobile && (
              <Stack direction="row" alignItems="center" spacing={0.1} sx={{ ml: 2 }}>
                <Button
                  size="medium"
                  onClick={handleOpportunitiesClick}
                  sx={{
                    color: "#0f172a",
                    backgroundColor: "transparent",
                    fontWeight: 550,
                    textTransform: "none",
                    ml: 1,
                    border: "1px solid transparent",
                    fontSize: "1.1rem",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                  }}
                >
                  Opportunities
                </Button>
              </Stack>
            )}
          </Box>

          {/* Right side - Work button or hamburger menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile ? (
              // Desktop - Work button
              <Button
                size="medium"
                onClick={handleWorkAction}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#3b82f6",
                  color: "#fff",
                  borderRadius: "0.5rem",
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  boxShadow: "none",
                  transition: "background-color 0.2s",
                  '&:hover': {
                    backgroundColor: '#2563eb',
                  },
                  marginRight: "3px",
                }}
              >
                Work
              </Button>
            ) : (
              // Mobile - Hamburger menu
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  color: "#0f172a",
                  '&:hover': {
                    backgroundColor: "#f1f5f9",
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: "#f9fafc",
            borderLeft: "1px solid #e2e8f0",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drawer Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#0f172a",
              }}
            >
              Menu
            </Box>
            <IconButton 
              onClick={handleDrawerClose}
              sx={{
                color: "#0f172a",
                '&:hover': {
                  backgroundColor: "#f1f5f9",
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Navigation Items */}
          <List sx={{ px: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleOpportunitiesClick();
                  handleDrawerClose();
                }}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <ListItemText 
                  primary="Opportunities"
                  primaryTypographyProps={{
                    fontWeight: 550,
                    fontSize: "1rem",
                    color: "#0f172a",
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>

          {/* Work Button */}
          <Divider sx={{ my: 2 }} />
          <Button
            fullWidth
            size="large"
            onClick={() => {
              handleWorkAction();
              handleDrawerClose();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: "#3b82f6",
              color: "#fff",
              borderRadius: "0.75rem",
              py: 1.5,
              fontSize: "1rem",
              boxShadow: "none",
              transition: "background-color 0.2s",
              '&:hover': {
                backgroundColor: '#2563eb',
              },
            }}
          >
            Work
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
